package com.iregained.site.components.sections.about.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.about.styles.AboutTrustContainerStyle
import com.iregained.site.components.sections.about.styles.TrustLogoStyle
import com.iregained.site.components.sections.home.sections.AccreditationData
import com.iregained.site.components.sections.home.styles.ClinicalAccreditationLogoStyle
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.components.widgets.VideoFormat
import com.iregained.site.components.widgets.YouTubeVideoCard
import com.iregained.site.theme.SubheadlineTextStyle
import com.iregained.site.theme.toSitePalette
import com.iregained.site.theme.unextractable
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.dom.Div

// UNIQUE ANIMATION: Center Expand
@Composable
private fun Modifier.centerExpand(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).scale(if (isVisible) 1f else 0.85f).transition(
        Transition.of("opacity", 0.7.s, TransitionTimingFunction.EaseOut, delayMs.ms),
        Transition.of("transform", 0.7.s, TransitionTimingFunction.EaseOut, delayMs.ms)
    )
}

@Composable
fun AboutTrustSection(data: AboutTrustSectionData = defaultAboutTrustData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "about-trust-section"
    var isVisible by remember { mutableStateOf(false) }

    if (data.sectionTitle.isNotBlank()) {
        Column(
            modifier = AboutTrustContainerStyle.toModifier().id(targetId).fillMaxWidth()
                .backgroundColor(palette.custom.lightGray)
                .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
                .styleModifier { property("scroll-margin-top", "100px") },
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Div(
                attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex)
                    .justifyContent(org.jetbrains.compose.web.css.JustifyContent.Center).toAttrs {
                        ref { element ->
                            val observer = IntersectionObserver { entries, obs ->
                                entries.forEach {
                                    if (it.isIntersecting) {
                                        isVisible = true; obs.unobserve(element)
                                    }
                                }
                            }
                            observer.observe(element); onDispose { observer.disconnect() }
                        }
                    }) {
                Column(
                    modifier = Modifier.fillMaxWidth().maxWidth(1440.px),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    SectionHeader(
                        text = data.sectionTitle,
                        fontSize = 32.px,
                        lineWidthPercent = 25.percent,
                        modifier = Modifier.margin(bottom = 16.px).opacity(if (isVisible) 1 else 0)
                            .transition(Transition.of("opacity", 0.8.s)),
                        alignment = Alignment.CenterHorizontally
                    )

                    if (data.sectionSubtitle.isNotBlank()) {
                        Div(
                            attrs = SubheadlineTextStyle.toModifier().maxWidth(1200.px).fontSize(18.px)
                                .fontWeight(FontWeight.Medium).color(palette.custom.darkText)
                                .textAlign(TextAlign.Center).margin(bottom = 40.px).opacity(if (isVisible) 1 else 0)
                                .transition(Transition.of("opacity", 0.8.s, delay = 150.ms)).toAttrs {
                                    ref { htmlElement ->
                                        htmlElement.innerHTML = data.sectionSubtitle; onDispose {}
                                    }
                                })
                    }

                    val hqEmbed = data.hqVideoEmbedUrl
                    if (!hqEmbed.isNullOrBlank()) {
                        Box(
                            modifier = Modifier.fillMaxWidth()
                                .maxWidth(if (data.hqVideoAspectRatio == VideoFormat.CINEMATIC_ULTRAWIDE) 1440.px else 1100.px)
                                .margin(bottom = 24.px).centerExpand(isVisible, 300),
                            contentAlignment = Alignment.Center
                        ) {
                            YouTubeVideoCard(
                                embedUrl = hqEmbed,
                                ariaLabel = data.hqVideoAriaLabel,
                                thumbnailUrl = data.hqVideoThumbnailUrl,
                                labelPillText = data.hqVideoLabelPillText,
                                videoFormat = data.hqVideoAspectRatio,
                                modifier = Modifier.fillMaxWidth().boxShadow(
                                    0.px, 20.px, 40.px, color = palette.custom.darkText.toRgb().copyf(alpha = 0.15f)
                                )
                            )
                        }
                    }

                    Row(
                        modifier = Modifier.fillMaxWidth().maxWidth(1440.px).display(DisplayStyle.Flex)
                            .flexWrap(FlexWrap.Wrap).justifyContent(JustifyContent.SpaceAround)
                            .alignItems(AlignItems.Center)
                    ) {
                        data.logos.forEachIndexed { index, accData ->
                            key(accData.altText) {
                                ClinicalAccreditationLogo(
                                    accData, Modifier.weight(1f).centerExpand(isVisible, 500 + (index * 150))
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun ClinicalAccreditationLogo(data: TrustLogoData, modifier: Modifier = Modifier) {
    Column(
        modifier = modifier.minWidth(250.px).padding(10.px),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        if (data.imagePath.isNotBlank()) {
            Image(src = data.imagePath, modifier = ClinicalAccreditationLogoStyle.toModifier().unextractable())
        }
        SpanText(
            text = data.regionName,
            modifier = Modifier
                .margin(top = 8.px)
                .fontSize(20.px)
                .fontWeight(FontWeight.Bold)
                .color(Colors.DarkSlateGray)
                .textAlign(TextAlign.Center)
        )
    }
}

// ==========================================
// CMS DATA MODEL & MEDIA SPECS
// ==========================================
/**
 * CMS IMAGE UPLOAD SPECS FOR TRUST LOGOS:
 * @field imagePath:
 * - Recommended Dimensions: Minimum height of 100px. Width can vary.
 * - Format: Transparent PNG or SVG (Highly Preferred).
 * - Content: Do not upload logos with white backgrounds! The background must be completely transparent for the grayscale/hover effect to look professional.
 *
 *  @field hqVideoEmbedUrl (OPTIONAL): A `https://www.youtube.com/embed/<ID>` URL.
 *  *   YouTube's own thumbnail is used automatically. Set to `null` to hide the block.
 *
 *  @field hqVideoCaption (OPTIONAL): short caption shown under the player.
 *
 */
data class TrustLogoData(
    val imagePath: String, val altText: String, val regionName: String
)

data class AboutTrustSectionData(
    val sectionId: String? = null,
    val sectionTitle: String = "Designed by Experts. Built in Canada.",
    val sectionSubtitle: String = "Take a look inside our headquarters where our team of Bio-Medical Engineers, Software Developers and Clinical Researchers build the standard of care under one roof.",
    val logos: List<TrustLogoData> = listOf(
        TrustLogoData(
            imagePath = Res.Images.Home.Accreditations.FDA_APPROVED, altText = "FDA", regionName = "United States"
        ),
        TrustLogoData(
            imagePath = Res.Images.Home.Accreditations.HEALTH_CANADA, altText = "Health Canada Approved", regionName = "Canada"
        ),
        TrustLogoData(
            imagePath = Res.Images.Home.Accreditations.CDSCO, altText = "CDSCO", regionName = "India"
        ),
        TrustLogoData(
            imagePath = Res.Images.Home.Accreditations.HSA, altText = "HSA", regionName = "Singapore"
        )
    ),
    val hqVideoEmbedUrl: String? = Res.Strings.Links.HQ_TOUR,
    val hqVideoThumbnailUrl: String? = null,
    val hqVideoLabelPillText: String? = "Watch · Inside our HQ",
    val hqVideoAriaLabel: String = "Play the IRegained HQ and team tour video",
    val hqVideoCaption: String? = "Inside our Greater Sudbury headquarters — engineering, manufacturing, and clinical validation under one roof.",
    val hqVideoAspectRatio: VideoFormat = VideoFormat.CINEMATIC_ULTRAWIDE,
)

val defaultAboutTrustData = AboutTrustSectionData()